import { toast } from '@apps/web/lib/app_notifications';

// The common (non-admin) sync buttons — e.g. the Shifts and Caregivers list
// panes — trigger an async worker sync whose trigger endpoint returns 202
// immediately. Without feedback the spinner flickers off instantly and the
// user can't tell whether anything happened. Keep the spinner up for a brief
// minimum so the click registers, then confirm via toast that the sync was
// started (or that starting it failed).
export const SYNC_INITIATION_MIN_SPINNER_MS = 400;

type RunSyncWithFeedbackOptions = {
    // Fires the sync trigger request. Its resolved value is ignored — only
    // success vs. failure matters, since the sync itself runs in the worker.
    readonly trigger: () => Promise<unknown>;
    readonly setIsSyncing: (isSyncing: boolean) => void;
    // Shown on success, e.g. "Shift sync started".
    readonly successMessage: string;
    // Optional per-caller error message (e.g. rate-limit wording). Falls back
    // to a generic message when omitted or when it returns undefined.
    readonly formatError?: (error: unknown) => string | undefined;
};

/**
 * Runs a data-sync trigger with consistent user feedback: a minimum-visible
 * spinner followed by a success or failure toast. Reuse this for the common
 * non-admin sync buttons so they behave identically.
 */
export async function runSyncWithFeedback(
    options: RunSyncWithFeedbackOptions,
): Promise<void> {
    options.setIsSyncing(true);
    const minSpinner = new Promise<void>((resolve) =>
        setTimeout(resolve, SYNC_INITIATION_MIN_SPINNER_MS),
    );
    try {
        await options.trigger();
        await minSpinner;
        toast.success(options.successMessage, {
            persistNotification: true,
            notificationType: 'data_sync_action',
        });
    } catch (error) {
        console.error('Error triggering sync:', error);
        await minSpinner;
        toast.error(
            options.formatError?.(error) ??
                'Failed to start sync. Please try again.',
            {
                persistNotification: true,
                notificationType: 'data_sync_action',
            },
        );
    } finally {
        options.setIsSyncing(false);
    }
}
